import React, { useState, useEffect, useRef } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import BadgePreviewModal from '../components/BadgePreviewModal';
import PendingPaymentList from '../components/PendingPaymentList';
import InvoicePreview from '../components/InvoicePreview';
import DynamicFilters from '../components/DynamicFilters';
import DynamicRegistrationsTable from '../components/DynamicRegistrationsTable';
import OfflineScanStatus from '../components/OfflineScanStatus';
import BulkUploadModal from '../components/BulkUploadModal';
import QRScanLinkGenerator from '../components/QRScanLinkGenerator';
import ScanStatsManager from '../components/ScanStatsManager';
import ExportColumnsDialog from '../components/ExportColumnsDialog';
import EventQrPdfDialog from '../components/EventQrPdfDialog';
import BulkEmailDialog from '../components/BulkEmailDialog';
import InviteLinksDialog from '../components/InviteLinksDialog';
import CertificatesTab from '../components/CertificatesTab';
import TeamMembersTab from '../components/TeamMembersTab';
import { ApiService } from '../utils/api';
import { useToast } from '../components/Toast';
import { useAuth } from '../contexts/AuthContext';
import { firstEventPage, resolveRoleUi } from '../utils/roleUi';
import { 
  Search, 
  Download, 
  QrCode, 
  Mail, 
  Phone, 
  Calendar,
  ArrowLeft,
  Eye,
  Printer,
  Users,
  CreditCard,
  CheckCircle,
  XCircle,
  Clock,
  Settings,
  Filter,
  AlertTriangle,
  FileText,
  UserPlus,
  MoreHorizontal,
  Plus,
  Upload,
  Lock,
  BarChart3,
  ChevronDown,
  MonitorSmartphone
} from 'lucide-react';
import { useEventRoute } from '../utils/eventRef';

const EventRegistrations = () => {
  const { eventId, eventRef } = useEventRoute();
  const [searchParams] = useSearchParams();
  // Current table filters/search, so Export CSV exports exactly what is listed.
  const [tableQuery, setTableQuery] = useState({});
  const [showBulkEmail, setShowBulkEmail] = useState(false);
  const [showInviteLinks, setShowInviteLinks] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showBadgePreview, setShowBadgePreview] = useState(false);
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [activeFilters, setActiveFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [sorting, setSorting] = useState({ sortBy: 'createdAt', sortDirection: 'desc' });
  const [showBadgePreviewModal, setShowBadgePreviewModal] = useState(false);
  const [selectedRegistrationForBadge, setSelectedRegistrationForBadge] = useState(null);
  const [selectedRegistrationQRForBadge, setSelectedRegistrationQRForBadge] = useState(null);
  const [showBadgeQRPreviewModal, setShowBadgeQRPreviewModal] = useState(false);
  const [activeTab, setActiveTab] = useState('registrations');
  const [showScanMenuOpen, setShowScanMenuOpen] = useState(false);
  const [showMoreMenuOpen, setShowMoreMenuOpen] = useState(false);
  
  const scanMenuRef = useRef(null);
  const moreActionsRef = useRef(null);
  const exportMenuRef = useRef(null);
  const [showExportMenuOpen, setShowExportMenuOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showQrPdf, setShowQrPdf] = useState(false);


  
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { user } = useAuth();

  // Fetch QR scan purposes (shared with QRScanLinkGenerator / QRScanFlowPage)
  const { data: qrPurposes = [], isLoading: qrPurposesLoading } = useQuery({
    queryKey: ['qrScanPurposes', eventId],
    queryFn: () => ApiService.getQRScanPurposes(eventId),
    enabled: !!eventId,
  });

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (scanMenuRef.current && !scanMenuRef.current.contains(event.target)) {
        setShowScanMenuOpen(false);
      }
      if (moreActionsRef.current && !moreActionsRef.current.contains(event.target)) {
        setShowMoreMenuOpen(false);
      }
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch event data
  const { data: event, isLoading: eventLoading } = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => ApiService.getEvent(eventId),
    enabled: !!eventId,
  });

  // Admins see every action; other roles see what the event's role settings allow.
  const ui = resolveRoleUi(event?.roleUi, user?.roles || []);
  const uiConfig = {
    showOnSpotRegistration: ui.can('onSpot'),
    showScanQr: ui.can('scanQr'),
    showCertificateScan: ui.can('certificateScan'),
    showDynamicBadge: ui.can('dynamicBadge'),
    showCopyDeskLink: ui.can('copyDeskLink'),
    showQrPdf: ui.can('qrPdf'),
    showEmailAttendees: ui.can('emailAttendees'),
    showInviteLinks: ui.can('inviteLinks'),
    showAnalytics: ui.can('analytics'),
    showScanStats: ui.can('scanStats'),
    showExportCsv: ui.can('export'),
    showExportColumns: ui.can('exportColumns'),
    showBulkUpload: ui.can('bulkUpload'),
    showBadgeConfig: ui.can('badgeConfig'),
    showLockers: ui.can('lockers'),
    showOpenDesk: ui.can('openDesk'),
    showMoreActions: ui.can('scanLinks'),
    showCertificatesTab: ui.can('certificatesTab'),
    showTeamTab: ui.can('teamTab'),
    showAccessCodesPage: ui.can('accessCodesPage'),
    showBackButton: ui.can('backButton'),
  };

  // Fetch badge configuration
  const { data: badgeConfig } = useQuery({
    queryKey: ['badgeConfig', eventId],
    queryFn: () => ApiService.getBadgeConfig(eventId),
    enabled: !!eventId,
  });

  // Fetch filter definitions
  const { data: filterDefinitions, isLoading: filtersLoading } = useQuery({
    queryKey: ['registrationFilters', eventId],
    queryFn: () => ApiService.getRegistrationFilters(eventId),
    enabled: !!eventId,
  });

  // Approve payment mutation
  const approvePaymentMutation = useMutation({
    mutationFn: (userId) => ApiService.approvePayment(eventId, userId),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['registrations', eventId]);
      showSuccess('Payment approved successfully!');
    },
    onError: (error) => {
      showError('Failed to approve payment. Please try again.');
    }
  });

  // Reject payment mutation
  const rejectPaymentMutation = useMutation({
    mutationFn: (userId) => ApiService.rejectPayment(eventId, userId),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['registrations', eventId]);
      showSuccess('Payment rejected.');
    },
    onError: (error) => {
      showError('Failed to reject payment. Please try again.');
    }
  });




  const handleFiltersChange = (newFilters) => {
    setActiveFilters(newFilters);
  };

  const handleClearFilters = () => {
    setActiveFilters({});
  };

  const handleSortChange = (newSorting) => {
    setSorting(newSorting);
  };

  const handleBulkUploadSuccess = (result) => {
    // Refresh registrations data
    queryClient.invalidateQueries(['registrations', eventId]);
    setShowBulkUpload(false);
  };

  const handleBadgePreview = (registration) => {
    setSelectedRegistrationForBadge(registration);
    setShowBadgePreviewModal(true);
  };

  const handleViewQr = (registration) => {
    setSelectedRegistrationQRForBadge(registration);
    setShowBadgeQRPreviewModal(true);
  };

  const handleScanPurposeClick = (purpose) => {
    if (!eventId || !purpose?.purpose) return;
    const url = `/admin/events/${eventRef}/scan?purpose=${encodeURIComponent(purpose.purpose)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    setShowScanMenuOpen(false);
  };

  // The browser downloads via a one-time link, so progress shows in its downloads bar and the page stays usable.
  const handleExport = async (format) => {
    setShowExportMenuOpen(false);
    setIsExporting(true);
    try {
      const { url } = await ApiService.createExportTicket(eventId, tableQuery, format);
      const frame = document.createElement('iframe');
      frame.style.display = 'none';
      frame.src = url;
      document.body.appendChild(frame);
      // Keep the frame long enough for the download to start; the response is an attachment, so nothing renders.
      setTimeout(() => frame.remove(), 5 * 60 * 1000);
      showSuccess(format === 'zip'
        ? 'Download started — check your browser downloads. Files are added as they are fetched.'
        : 'Download started — check your browser downloads.');
    } catch (error) {
      console.error('Export failed:', error);
      showError('Failed to start the export. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  if (eventLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!ui.can('registrationsPage')) {
    const page = firstEventPage(ui);
    return page
      ? <Navigate to={`/admin/events/${eventRef}/${page}`} replace />
      : <p className="p-8 text-center text-gray-600">No pages of this event are available for your role.</p>;
  }

  if (!event) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Event Not Found</h2>
        <p className="text-gray-600 mb-4">The event you're looking for doesn't exist.</p>
        <Link to="/admin/events">
          <Button>Back to Events</Button>
        </Link>
      </div>
    );
  }

  const toolbarButtonClass = 'flex items-center gap-1.5 whitespace-nowrap';
  const menuItemClass = 'w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2';
  const hasMoreMenuItems = uiConfig.showBulkUpload || uiConfig.showBadgeConfig || uiConfig.showMoreActions
    || uiConfig.showOpenDesk || (event?.lockerEnabled && uiConfig.showLockers);
  const deskUrl = `${window.location.origin}/desk/${eventRef}`;
  const copyDeskLink = async () => {
    try {
      await navigator.clipboard.writeText(deskUrl);
      showSuccess(event?.deskPublic
        ? 'Desk link copied. Anyone with it can use the desk.'
        : 'Desk link copied. Staff need a viewer login unless kiosk mode is on in event settings.');
    } catch (_) {
      showError(`Copy failed. Desk link: ${deskUrl}`);
    }
  };

  // Determine if certificate scan purpose exists
  const certificatePurpose = qrPurposes.find(p => 
    p.shouldPrintCertificate ||
    p.purpose === 'certificate' ||
    p.name?.toLowerCase().includes('certificate')
  );
  const hasToolbar = uiConfig.showOnSpotRegistration || uiConfig.showScanQr || uiConfig.showDynamicBadge
    || uiConfig.showCopyDeskLink || uiConfig.showQrPdf || uiConfig.showEmailAttendees || uiConfig.showInviteLinks
    || uiConfig.showAnalytics || uiConfig.showScanStats || uiConfig.showExportCsv || uiConfig.showExportColumns
    || uiConfig.showBulkUpload || uiConfig.showBadgeConfig || uiConfig.showOpenDesk || uiConfig.showMoreActions
    || (event?.lockerEnabled && uiConfig.showLockers) || (!!certificatePurpose && uiConfig.showCertificateScan);

  return (
    <div className="space-y-4 sm:space-y-6 p-2 sm:p-4 lg:p-6">
      {/* Header */}
      <div className="space-y-4">
        {/* Main Title Section */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
            {uiConfig.showBackButton && (
              <Link to="/admin/events">
                <Button variant="outline" size="sm" className="w-fit">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              </Link>
            )}
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 break-words">{event.name}</h1>
              <p className="text-sm sm:text-base text-gray-600">Registrations</p>
            </div>
          </div>
          {ui.isAdmin && (
            <Link to={`/admin/events/${eventRef}/settings`}>
              <Button size="sm" variant="outline" className="w-full sm:w-auto">
                <Settings className="w-4 h-4 mr-2" />
                Event settings
              </Button>
            </Link>
          )}
        </div>

        {/* Action toolbar: event-day actions on the left, data tools on the right, rarely used items under More */}
        {hasToolbar && (
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
          <div className="flex flex-wrap items-center gap-2">
            {uiConfig.showOnSpotRegistration && (
              <Link to={`/register/${eventRef}/on-spot?enableCta=true`} target="_blank" rel="noopener noreferrer">
                <Button size="sm" className={toolbarButtonClass}>
                  <UserPlus className="w-4 h-4" />
                  <span>On-Spot Registration</span>
                </Button>
              </Link>
            )}

            {uiConfig.showScanQr && !qrPurposesLoading && qrPurposes.length === 0 && (
              <Button size="sm" disabled variant="outline" className={toolbarButtonClass}>
                <QrCode className="w-4 h-4" />
                <span>No Scan Purposes</span>
              </Button>
            )}

            {/* 1–2 purposes get direct buttons; more collapse into a Scan menu */}
            {uiConfig.showScanQr && !qrPurposesLoading && qrPurposes.length > 0 && qrPurposes.length <= 2 && (
              qrPurposes.map((purpose) => (
                <Link
                  key={purpose.purpose}
                  to={`/admin/events/${eventRef}/scan?purpose=${encodeURIComponent(purpose.purpose)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button size="sm" variant="outline" className={toolbarButtonClass}>
                    <QrCode className="w-4 h-4" />
                    <span>{`Scan ${purpose.name}`}</span>
                  </Button>
                </Link>
              ))
            )}

            {uiConfig.showScanQr && !qrPurposesLoading && qrPurposes.length > 2 && (
              <div className="relative" ref={scanMenuRef}>
                <Button
                  size="sm"
                  variant="outline"
                  className={toolbarButtonClass}
                  onClick={() => setShowScanMenuOpen((open) => !open)}
                >
                  <QrCode className="w-4 h-4" />
                  <span>Scan QR</span>
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                </Button>
                {showScanMenuOpen && (
                  <div className="absolute left-0 z-40 mt-2 min-w-[12rem] max-w-[calc(100vw-2rem)] max-h-[60vh] overflow-y-auto overscroll-contain bg-white border border-gray-200 rounded-lg shadow-lg py-1">
                    {qrPurposes.map((purpose) => (
                      <button
                        key={purpose.purpose}
                        type="button"
                        className={menuItemClass}
                        onClick={() => handleScanPurposeClick(purpose)}
                      >
                        <QrCode className="w-4 h-4" />
                        <span>{`Scan ${purpose.name}`}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {certificatePurpose && uiConfig.showCertificateScan && (
              <Link
                to={`/admin/events/${eventRef}/registrations?purpose=certificate`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button size="sm" variant="outline" className={`${toolbarButtonClass} text-purple-700 border-purple-300 hover:bg-purple-50`}>
                  <FileText className="w-4 h-4" />
                  <span>Certificate</span>
                </Button>
              </Link>
            )}

            {uiConfig.showDynamicBadge && (
              <Link to={`/admin/events/${eventRef}/badge-config`}>
                <Button size="sm" className={`${toolbarButtonClass} bg-purple-600 hover:bg-purple-700`}>
                  <span aria-hidden="true">🪄</span>
                  <span>Dynamic Badge</span>
                </Button>
              </Link>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 lg:justify-end">
            {uiConfig.showCopyDeskLink && (
              <Button size="sm" variant="outline" className={toolbarButtonClass} onClick={copyDeskLink} title={deskUrl}>
                <MonitorSmartphone className="w-4 h-4" />
                <span>Copy Desk Link</span>
              </Button>
            )}
            {uiConfig.showQrPdf && (
              <Button size="sm" variant="outline" className={toolbarButtonClass} onClick={() => setShowQrPdf(true)}>
                <QrCode className="w-4 h-4" />
                <span>QR Codes PDF</span>
              </Button>
            )}
            {uiConfig.showEmailAttendees && (
              <Button size="sm" variant="outline" className={toolbarButtonClass} onClick={() => setShowBulkEmail(true)}>
                <Mail className="w-4 h-4" />
                <span>Email Attendees</span>
              </Button>
            )}
            {uiConfig.showInviteLinks && (
              <Button size="sm" variant="outline" className={toolbarButtonClass} onClick={() => setShowInviteLinks(true)}>
                <Users className="w-4 h-4" />
                <span>Invite Links</span>
              </Button>
            )}
            <InviteLinksDialog open={showInviteLinks} onClose={() => setShowInviteLinks(false)} eventId={eventId} />
            <BulkEmailDialog
              open={showBulkEmail}
              onClose={() => setShowBulkEmail(false)}
              eventId={eventId}
              filter={tableQuery}
              formSchema={[...(event?.formSchema || []), ...(event?.onSpotFormSchema || [])]}
            />
            <EventQrPdfDialog
              open={showQrPdf}
              onClose={() => setShowQrPdf(false)}
              eventId={eventId}
              eventName={event?.name}
              purposes={qrPurposes}
            />
            {uiConfig.showAnalytics && (
              <Link to={`/admin/events/${eventRef}/analytics`}>
                <Button size="sm" variant="outline" className={toolbarButtonClass}>
                  <BarChart3 className="w-4 h-4" />
                  <span>Analytics</span>
                </Button>
              </Link>
            )}
            {uiConfig.showScanStats && (
              <ScanStatsManager
                eventId={eventId}
                eventName={event?.name}
                size="sm"
                triggerClassName={toolbarButtonClass}
              />
            )}
            {uiConfig.showExportCsv && (
              <div className="relative" ref={exportMenuRef}>
                <Button
                  size="sm"
                  variant="outline"
                  className={toolbarButtonClass}
                  onClick={() => setShowExportMenuOpen((open) => !open)}
                  disabled={isExporting}
                >
                  <Download className="w-4 h-4" />
                  <span>{isExporting ? 'Preparing…' : 'Export'}</span>
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                </Button>
                {showExportMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-64 max-w-[calc(100vw-2rem)] max-h-[70vh] overflow-y-auto overscroll-contain bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                    <button type="button" className={menuItemClass} onClick={() => handleExport('csv')}>
                      <Download className="w-4 h-4" />
                      <span>CSV</span>
                    </button>
                    <button type="button" className={menuItemClass} onClick={() => handleExport('xlsx')}>
                      <Download className="w-4 h-4" />
                      <span>Excel (.xlsx)</span>
                    </button>
                    <button type="button" className={menuItemClass} onClick={() => handleExport('xlsx-analytics')}>
                      <Download className="w-4 h-4" />
                      <span className="text-left">
                        Excel with analytics (.xlsx)
                        <span className="block text-xs text-gray-500">Scan columns: actual, manually attributed, final</span>
                      </span>
                    </button>
                    <button type="button" className={menuItemClass} onClick={() => handleExport('zip')}>
                      <Download className="w-4 h-4" />
                      <span className="text-left">
                        Excel + documents (.zip)
                        <span className="block text-xs text-gray-500">Every uploaded file, linked from the sheet</span>
                      </span>
                    </button>
                  </div>
                )}
              </div>
            )}
            {uiConfig.showExportColumns && (
              <ExportColumnsDialog eventId={eventId} size="sm" triggerClassName={toolbarButtonClass} />
            )}

            {hasMoreMenuItems && (
              <div className="relative" ref={moreActionsRef}>
                <Button
                  size="sm"
                  variant="outline"
                  className={toolbarButtonClass}
                  onClick={() => setShowMoreMenuOpen((open) => !open)}
                  aria-label="More actions"
                >
                  <MoreHorizontal className="w-4 h-4" />
                  <span>More</span>
                </Button>
                {showMoreMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 max-w-[calc(100vw-2rem)] max-h-[70vh] overflow-y-auto overscroll-contain bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                    {uiConfig.showBulkUpload && (
                      <button
                        type="button"
                        className={menuItemClass}
                        onClick={() => {
                          setShowMoreMenuOpen(false);
                          setShowBulkUpload(true);
                        }}
                      >
                        <Upload className="w-4 h-4" />
                        <span>Bulk Upload</span>
                      </button>
                    )}
                    {uiConfig.showBadgeConfig && (
                      <Link to={`/admin/events/${eventRef}/badges`} className={menuItemClass}>
                        <Settings className="w-4 h-4" />
                        <span>Badge Config</span>
                      </Link>
                    )}
                    {event?.lockerEnabled && uiConfig.showLockers && (
                      <Link to={`/admin/events/${eventRef}/lockers`} className={menuItemClass}>
                        <Lock className="w-4 h-4" />
                        <span>Lockers</span>
                      </Link>
                    )}
                    {uiConfig.showOpenDesk && (
                      <a href={deskUrl} target="_blank" rel="noopener noreferrer" className={menuItemClass}>
                        <MonitorSmartphone className="w-4 h-4" />
                        <span>Open Desk</span>
                      </a>
                    )}
                    {uiConfig.showMoreActions && (
                      <QRScanLinkGenerator
                        eventId={eventId}
                        eventName={event.name}
                        className={menuItemClass}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Tab Navigation */}
      <Card>
        {(uiConfig.showCertificatesTab || uiConfig.showTeamTab) && (
        <CardHeader className="p-0">
          <div className="flex items-center overflow-x-auto border-b border-gray-200">
            <button
              onClick={() => setActiveTab('registrations')}
              className={`flex items-center space-x-2 px-3 sm:px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'registrations'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Users className="w-4 h-4" />
              <span>Registrations</span>
            </button>
            {uiConfig.showCertificatesTab && (
              <button
                onClick={() => setActiveTab('certificates')}
                className={`flex items-center space-x-2 px-3 sm:px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'certificates'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FileText className="w-4 h-4" />
                <span>Certificates</span>
              </button>
            )}
            {uiConfig.showTeamTab && (
              <button
                onClick={() => setActiveTab('team')}
                className={`flex items-center space-x-2 px-3 sm:px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'team'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <UserPlus className="w-4 h-4" />
                <span>Team</span>
              </button>
            )}
          </div>
        </CardHeader>
        )}
        <CardContent className="p-0">
          {/* Tab Content */}
          {activeTab === 'registrations' && (
            <div className="p-6 space-y-4">
              {/* Offline cache + background-sync status (also primes the cache) */}
              <OfflineScanStatus eventId={eventId} offlineEnabled={!!event?.offlineEnabled} />

              {/* Dynamic Filters */}
      {/* {filterDefinitions && filterDefinitions.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Filters</h3>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2"
            >
              <Filter className="h-4 w-4" />
              <span>{showFilters ? 'Hide' : 'Show'} Filters</span>
            </Button>
          </div>
          {showFilters && (
            <DynamicFilters
              filters={filterDefinitions}
              onFiltersChange={handleFiltersChange}
              onClearFilters={handleClearFilters}
              onSortChange={handleSortChange}
            />
          )}
        </div>
      )} */}


              {/* Registrations Table */}
              <DynamicRegistrationsTable 
                eventId={eventId} 
                onQueryChange={setTableQuery}
                rfIdEnabled={!!event?.rfIdEnabled}
                lockerEnabled={!!event?.lockerEnabled}
                showConfirmationEmailButton={event?.showConfirmationEmailButton !== false}
                roleUi={ui}
              />
            </div>
          )}
          
          {activeTab === 'certificates' && uiConfig.showCertificatesTab && (
            <div className="p-6">
              <CertificatesTab eventId={eventId} eventName={event?.name} />
            </div>
          )}

          {activeTab === 'team' && uiConfig.showTeamTab && (
            <div className="p-6">
              <TeamMembersTab eventId={eventId} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoice Preview Modal */}
      {showInvoicePreview && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Invoice Preview</h3>
                <Button
                  variant="outline"
                  onClick={() => setShowInvoicePreview(false)}
                >
                  Close
                </Button>
              </div>
              <InvoicePreview
                invoice={selectedInvoice}
                onDownload={() => console.log('Download invoice')}
                onPrint={() => window.print()}
                onEmail={() => console.log('Email invoice')}
              />
            </div>
          </div>
        </div>
      )}

      {/* QR Preview Modal */}
{showBadgeQRPreviewModal && selectedRegistrationQRForBadge && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-auto">
      <div className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">QR Code</h3>
          <Button
            variant="outline"
            onClick={() => setShowBadgeQRPreviewModal(false)}
          >
            Close
          </Button>
        </div>

        {/* QR Code Image */}
        {selectedRegistrationQRForBadge.qrCodeBase64 ? (
          <div className="flex justify-center items-center">
            <img
              src={`data:image/png;base64,${selectedRegistrationQRForBadge.qrCodeBase64}`}
              alt="QR Code"
              className="w-48 h-48 border rounded-lg shadow"
            />
          </div>
        ) : (
          <p className="text-center text-gray-500">No QR available</p>
        )}
      </div>
    </div>
  </div>
)}


      {/* Bulk Upload Modal */}
      <BulkUploadModal
        eventId={eventId}
        formSchema={event?.formSchema || []}
        isOpen={showBulkUpload}
        onClose={() => setShowBulkUpload(false)}
        onSuccess={handleBulkUploadSuccess}
      />

      {/* Badge Preview Modal */}
      <BadgePreviewModal
        eventId={eventId}
        registrationId={selectedRegistrationForBadge?.userId}
        isOpen={showBadgePreviewModal}
        onClose={() => {
          setShowBadgePreviewModal(false);
          setSelectedRegistrationForBadge(null);
        }}
        registrationData={selectedRegistrationForBadge?.formData}
      />
    </div>
  );
};

export default EventRegistrations;
